import type { ComponentProps, MouseEvent } from 'react';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { display, marginBottom, width } from 'styled-system';
import type { DisplayProps, MarginBottomProps, WidthProps } from 'styled-system';

import { Flex } from 'common/components/Flex';
import { Grid, GridItem } from 'common/components/Grid';
import { ResponsiveLazyImage } from 'common/components/LazyImage';
import type { ResponsiveLazyImageProps } from 'common/components/LazyImage/types';
import type { PrismicImage } from 'common/components/PrismicResponsiveImage';
import { useMatchesViewport } from 'common/hooks/media';
import { aspectRatio as aspectRatioUtil } from 'common/theme/system-utilities';
import type { AspectRatioProps } from 'common/theme/system-utilities';
import { DesktopTooltip } from './components/DesktopTooltip';
import { Dot } from './components/Dot';
import { MobileCarousel } from './components/MobileCarousel';
import ResponsiveImage from './components/ResponsiveImage';
import { dispatchInspirationItemNavigatedEvent, dispatchMarkerClickEvent } from './tracking';
import type { ShopTheLookProps } from './types';

const PictureContainer = styled.div<
  WidthProps & DisplayProps & AspectRatioProps & MarginBottomProps
>`
  ${width};
  ${display};
  ${aspectRatioUtil};
  ${marginBottom};
`;

const Picture = styled(ResponsiveLazyImage)<WidthProps & DisplayProps>`
  ${width};
  ${display};
`;

// Old format uses single imageMarkers array, so use consistent aspect ratio across all viewports
const pictureContainerAspectRatioStyles: ComponentProps<typeof PictureContainer> = {
  width: '100%',
  display: 'block',
  aspectRatio: '8 / 3', // Use desktop aspect ratio consistently for old format
};

const pictureStyles: ComponentProps<typeof Picture> = {
  width: '100%',
  display: 'block',
};

const pictureContainerStyles: ComponentProps<typeof Flex> = {
  position: 'relative',
};

// Type guard to check if image is PrismicImage
// Old format (ResponsiveLazyImageProps) has 'thumbnail' at root level from transformFullImageSet
// New format (PrismicImage) does not have 'thumbnail' at root level
const isNewImageFormat = (
  image: PrismicImage | ResponsiveLazyImageProps
): image is PrismicImage => {
  return !('thumbnail' in image);
};

// eslint-disable-next-line max-lines-per-function, complexity
const ShopTheLook = ({
  image,
  imageMarkers,
  mobileImageMarkers,
  desktopImageMarkers,
  list,
}: ShopTheLookProps) => {
  const [activeMarkerIndex, setActiveMarkerIndex] = useState<Nullable<number>>(0);
  const [shouldMoveFocus, setShouldMoveFocus] = useState(false);
  const hideTimeoutRef = useRef<Nullable<NodeJS.Timeout>>(null);
  const tooltipLinkRef = useRef<HTMLAnchorElement>(null);
  const activeDotRef = useRef<HTMLButtonElement>(null);
  const tooltipId = useId();

  const {
    matches: { matchesDesktop },
  } = useMatchesViewport();
  const isMobileOrTablet = !matchesDesktop;

  // Support both old format (imageMarkers) and new format (mobileImageMarkers/desktopImageMarkers)
  const markers =
    mobileImageMarkers && desktopImageMarkers
      ? isMobileOrTablet
        ? mobileImageMarkers
        : desktopImageMarkers
      : imageMarkers;

  useEffect(() => {
    if (
      !isMobileOrTablet &&
      shouldMoveFocus &&
      activeMarkerIndex !== null &&
      tooltipLinkRef.current
    ) {
      const focusTimeout = setTimeout(() => {
        tooltipLinkRef.current?.focus();
        setShouldMoveFocus(false);
      }, 50);

      return () => clearTimeout(focusTimeout);
    }
  }, [shouldMoveFocus, activeMarkerIndex, isMobileOrTablet]);

  const activeMarker = activeMarkerIndex !== null ? markers?.[activeMarkerIndex] : null;

  const handleCarouselCardClick = useCallback(
    (index: number) => (e: MouseEvent) => {
      if (index !== activeMarkerIndex) {
        const markerArticleTracking = markers?.[index]?.article.tracking;
        if (markerArticleTracking && list) {
          dispatchInspirationItemNavigatedEvent({
            tracking: markerArticleTracking,
            list,
            position: index,
          });
        }
        setActiveMarkerIndex(index);
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [activeMarkerIndex, markers, list]
  );

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(
    (index: number) => () => {
      if (!isMobileOrTablet) {
        clearHideTimeout();
        setActiveMarkerIndex(index);
      }
    },
    [isMobileOrTablet, clearHideTimeout]
  );

  const handleMouseLeave = useCallback(() => {
    if (!isMobileOrTablet) {
      hideTimeoutRef.current = setTimeout(() => {
        setActiveMarkerIndex(null);
      }, 200);
    }
  }, [isMobileOrTablet]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };
  }, []);

  const handleFocus = useCallback(
    (index: number) => () => {
      if (!isMobileOrTablet) {
        clearHideTimeout();
        setActiveMarkerIndex(index);
        setShouldMoveFocus(false); // Just show tooltip, don't move focus
      }
    },
    [isMobileOrTablet, clearHideTimeout]
  );

  const handleEscape = useCallback(() => {
    setActiveMarkerIndex(null);
    setShouldMoveFocus(false);
    activeDotRef.current?.focus();
  }, []);

  return (
    <Grid data-corgi-id="shop-the-look" aria-hidden="true">
      <GridItem>
        <Flex width={1} {...pictureContainerStyles}>
          {markers?.map((marker, index) => (
            <Dot
              key={`key-${marker.x}-${marker.y}`}
              x={marker.x}
              y={marker.y}
              onClick={() => {
                setActiveMarkerIndex(index);
                if (!isMobileOrTablet) {
                  // Move keyboard focus on selected (desktop only)
                  setShouldMoveFocus(true);
                }
                dispatchMarkerClickEvent(
                  marker.article.name,
                  marker.article.variantName,
                  marker.article.sku
                );
              }}
              onMouseEnter={handleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
              onFocus={handleFocus(index)}
              isActive={index === activeMarkerIndex}
              ariaDescribedBy={index === activeMarkerIndex ? tooltipId : undefined}
              dotRef={index === activeMarkerIndex ? activeDotRef : undefined}
            />
          ))}

          {isNewImageFormat(image) ? (
            <ResponsiveImage image={image} />
          ) : (
            <PictureContainer {...pictureContainerAspectRatioStyles}>
              <Picture {...pictureStyles} {...image} alt={image.alt || ''} />
            </PictureContainer>
          )}

          {!isMobileOrTablet && activeMarker && (
            <DesktopTooltip
              activeMarker={activeMarker}
              activeMarkerIndex={activeMarkerIndex ?? 0}
              list={list}
              onMouseEnter={handleMouseEnter(activeMarkerIndex)}
              onMouseLeave={handleMouseLeave}
              id={tooltipId}
              linkRef={tooltipLinkRef}
              onEscape={handleEscape}
            />
          )}
        </Flex>

        {isMobileOrTablet && markers?.length > 0 && (
          <div>
            <MobileCarousel
              markers={markers}
              activeMarkerIndex={activeMarkerIndex}
              list={list}
              onCardClick={handleCarouselCardClick}
            />
          </div>
        )}
      </GridItem>
    </Grid>
  );
};

export default ShopTheLook;
